// Mẫu block, ảnh thiết kế (hacom-new) và kiểu dữ liệu cho màn Block trang chủ.
// Danh sách block thật lấy từ API TblHome (store/useHomeBlockStore.ts).

export type HomeBlockTemplate =
  | "hero"
  | "catgrid"
  | "persona"
  | "product"
  | "banner"
  | "tiles"
  | "business"
  | "service"
  | "numbers"
  | "news"
  | "video"
  | "gallery"
  | "promo";

export interface HomeBlock {
  // TBL_HOME.ID (chuỗi GUID do API sinh)
  id: string;
  // Thứ tự đang lưu trên hệ thống (TBL_HOME.STT); thứ tự trên màn là vị trí trong danh sách
  stt?: number;
  // API chưa trả TEMPLATE và không đoán được từ mã block: không mở trình sửa, không cho lưu để khỏi ghi sai mẫu
  templateMissing?: boolean;
  name: string;
  template: HomeBlockTemplate;
  code: string;
  thumbId: string;
  desktop: boolean;
  mobile: boolean;
  visible: boolean;
  from: Date | null;
  to: Date | null;
  // Cột CONFIG (JSON): mọi cấu hình chi tiết của block, kiểu theo mẫu ở homeBlockConfig.ts.
  // Các trường ở trên là metadata, lưu cột riêng. undefined = chưa mở trình sửa lần nào.
  config?: unknown;
}

export interface TemplateFieldGroup {
  title: string;
  fields: string;
  source?: string;
}

export interface HomeBlockTemplateInfo {
  key: HomeBlockTemplate;
  name: string;
  desc: string;
  used: string;
  thumbId: string;
  defaultName: string;
  hasEditor: boolean;
  groups: TemplateFieldGroup[];
}

export const ACCENT = "#0c68b4";

// Ảnh chụp thiết kế từng block (Google Drive của bản mockup hacom-new)
export const DESIGN_IMAGE = {
  hero: "1q7xmObwTZ20PT61GiPjrnCWFsDRmruLA",
  catgrid: "1_e3ttRNse8jx6NkTHN-u0wthVUbel_-y",
  persona: "1SSzjKYHhPv8aF-VeFj2YJLz1PT965blD",
  laptop: "1GjQlPJMnc-vJHoR3BA81agNuF5gie1TU",
  pc: "1yXvOwtJzGZt5buCFnAWX7Yun6hUq2yli",
  pcBuilder: "1qHIp7WM3VrMmSUH8-aaE_9F2hDfDQB8k",
  monitor: "1lheOGdUoKvd7KS85sPg0OGDBaGIB7Mex",
  pcParts: "1QMdpg4IRboGo3yYeu-IwTsDhufscTrSJ",
  gamingGear: "1ZWy_bBNXEuJkQLh5j8jDgHBEjiXkkxJH",
  console: "1NM5Ib7AA4gCmw9jOK4jsoenDzHiGqXPu",
  network: "15Ie21T22kTBzHuZ4v1kJrybrfwoHvAuD",
  office: "1aWogHnpxvjEjZrhuXLy2iwAFOpY319yA",
  smartHome: "1wT7ntwp0_QKIxmEYAFFqPWYO3jq2kHkl",
  business: "12t-jLMtfaRTzAXnUAVHmvnEhgssPGWE6",
  service: "1G8a3-GzpfF2w21m2u6vCGdSsLejkQb0m",
  numbers: "1dOcXpDb5iAM1Yo5jwTMMOm1Aw893IL1E",
  news: "1e6h0Zgv6PN1ml87Fek9RYcElBR_0iu-D",
  video: "1CPjhuvcHrjgyvYv8X0Y5qkar72cgcdER",
  gallery: "1LYjg-4IOsS83ykKdjItT6Gg3mDdO1cxE",
  promo: "11k1TdxKkZWw5h2r7cY0g5MZzF0S9bo2a",
};

export const designImage = (id: string, width = 720) =>
  `https://lh3.googleusercontent.com/d/${id}=w${width}`;

export const COMMON_FIELDS: { title: string; desc: string }[] = [
  {
    title: "Tên và mã block",
    desc: "Mã duy nhất, dùng làm id neo trên web, không đổi sau khi tạo.",
  },
  {
    title: "Thứ tự và bật tắt",
    desc: "Kéo thả trong danh sách; block tắt vẫn giữ nội dung.",
  },
  { title: "Thiết bị", desc: "Hiện trên máy tính, điện thoại, hoặc cả hai." },
  { title: "Lịch hiển thị", desc: "Hiện từ, đến. Để trống là luôn hiển thị." },
  {
    title: "Lưu",
    desc: "Thay đổi chỉ ghi xuống hệ thống khi bấm Lưu.",
  },
];

export const TEMPLATES: HomeBlockTemplateInfo[] = [
  {
    key: "hero",
    name: "Hero",
    desc: "Slider chính, 3 banner phụ, dải cam kết.",
    used: "Banner đầu trang",
    thumbId: DESIGN_IMAGE.hero,
    defaultName: "Banner đầu trang",
    hasEditor: true,
    groups: [
      {
        title: "Slider chính",
        fields: "Slide: ảnh máy tính, ảnh điện thoại, link, mô tả ảnh, lịch riêng.",
        source: "Danh sách banner, vị trí Slider trang chủ",
      },
      {
        title: "3 banner phụ",
        fields: "Mỗi ô: ảnh máy tính, ảnh điện thoại, link, mô tả ảnh.",
        source: "Danh sách banner",
      },
      {
        title: "Dải cam kết",
        fields: "Tối đa 6 mục (icon, tiêu đề, mô tả) và số tổng đài.",
      },
    ],
  },
  {
    key: "catgrid",
    name: "Lưới danh mục",
    desc: "Ô quảng bá và lưới danh mục kèm liên kết con.",
    used: "Danh mục nổi bật",
    thumbId: DESIGN_IMAGE.catgrid,
    defaultName: "Danh mục nổi bật",
    hasEditor: true,
    groups: [
      {
        title: "Đầu block",
        fields:
          "Nhãn nhỏ, tiêu đề, phần tô màu trong tiêu đề, mô tả, nút Xem tất cả.",
      },
      { title: "Ô quảng bá", fields: "Ảnh, tiêu đề, 3 gạch đầu dòng, nút và link." },
      {
        title: "Ô danh mục",
        fields: "Chọn danh mục, ảnh, icon, 3 liên kết con. Số sản phẩm tự đếm.",
        source: "Danh mục sản phẩm",
      },
    ],
  },
  {
    key: "persona",
    name: "Thẻ nhu cầu",
    desc: "Thẻ ảnh theo nhóm khách hàng, gợi ý nhóm sản phẩm.",
    used: "Chọn máy theo nghề nghiệp",
    thumbId: DESIGN_IMAGE.persona,
    defaultName: "Chọn máy theo nhu cầu",
    hasEditor: true,
    groups: [
      { title: "Đầu block", fields: "Tiêu đề, mô tả, tối đa 3 cam kết nhỏ." },
      {
        title: "Thẻ",
        fields:
          "Ảnh, nhóm khách hàng, icon, 3 gạch đầu dòng, 3 gợi ý (nhãn, icon, link), nút.",
        source: "Danh mục sản phẩm",
      },
    ],
  },
  {
    key: "product",
    name: "Ngành hàng sản phẩm",
    desc: "Banner, thương hiệu, ô danh mục nhanh, tab sản phẩm.",
    used: "Laptop, PC, Màn hình, Văn phòng",
    thumbId: DESIGN_IMAGE.laptop,
    defaultName: "Ngành hàng mới",
    hasEditor: true,
    groups: [
      {
        title: "Banner lớn",
        fields: "Ảnh máy tính, ảnh điện thoại, link, mô tả ảnh.",
        source: "Danh sách banner",
      },
      {
        title: "Thương hiệu",
        fields: "Chọn và sắp xếp logo hãng.",
        source: "Danh sách thương hiệu",
      },
      { title: "Dải thông báo", fields: "Tuỳ chọn: tiêu đề, mô tả, nút và link." },
      {
        title: "Ô danh mục nhanh",
        fields: "Ảnh, tên, link. Tối đa 6 ô.",
        source: "Danh mục sản phẩm",
      },
      {
        title: "Tab sản phẩm",
        fields:
          "Tên tab, nguồn (danh mục, bộ sưu tập, chọn tay), sắp xếp, số sản phẩm, nhãn trên thẻ.",
        source: "Danh mục sản phẩm, Bộ sưu tập",
      },
    ],
  },
  {
    key: "banner",
    name: "Banner",
    desc: "Một banner lớn có nút hành động, ảnh riêng cho điện thoại.",
    used: "HACOM PC Builder",
    thumbId: DESIGN_IMAGE.pcBuilder,
    defaultName: "Banner",
    hasEditor: true,
    groups: [
      {
        title: "Nội dung",
        fields: "Ảnh máy tính, ảnh điện thoại, link, mô tả ảnh, nhãn nút.",
        source: "Danh sách banner",
      },
    ],
  },
  {
    key: "tiles",
    name: "Lưới ô",
    desc: "Ô ảnh nhiều kích thước dẫn tới danh mục hoặc trang.",
    used: "Linh kiện, Gaming Gear, Console, Mạng, Nhà thông minh",
    thumbId: DESIGN_IMAGE.gamingGear,
    defaultName: "Lưới ô",
    hasEditor: true,
    groups: [
      {
        title: "Bố cục",
        fields:
          "Chọn khung lưới: 1 lớn và 6 nhỏ, ô lớn ở giữa, 2 hàng tự do, 4 cột đều.",
      },
      {
        title: "Ô",
        fields: "Ảnh, tiêu đề, mô tả 2 dòng, link. Ô lớn thêm nhãn nhỏ và nút.",
        source: "Danh sách media",
      },
      {
        title: "Khối danh mục phụ",
        fields: "Tuỳ chọn, dùng cho Nhà thông minh: ô danh mục có ảnh.",
        source: "Danh mục sản phẩm",
      },
    ],
  },
  {
    key: "business",
    name: "Logo và giải pháp",
    desc: "Lĩnh vực, logo khách hàng, quyền lợi, nhóm dịch vụ.",
    used: "Khách hàng doanh nghiệp",
    thumbId: DESIGN_IMAGE.business,
    defaultName: "Khách hàng doanh nghiệp",
    hasEditor: true,
    groups: [
      {
        title: "Đầu block",
        fields: "Tiêu đề, mô tả, 2 nút (giải pháp, liên hệ tư vấn).",
      },
      { title: "Lĩnh vực và quyền lợi", fields: "Icon và nhãn cho từng mục." },
      {
        title: "Logo khách hàng",
        fields: "Logo, tên, link. Kéo thả thứ tự.",
        source: "Danh sách media",
      },
      { title: "Nhóm dịch vụ", fields: "Ảnh, tiêu đề, mô tả, link." },
    ],
  },
  {
    key: "service",
    name: "Dịch vụ",
    desc: "Danh mục dịch vụ, cam kết, quy trình theo bước.",
    used: "HACOM dịch vụ tốt",
    thumbId: DESIGN_IMAGE.service,
    defaultName: "Dịch vụ",
    hasEditor: true,
    groups: [
      { title: "Đầu block", fields: "Tiêu đề, khẩu hiệu, ảnh nền, 4 cam kết." },
      { title: "Danh mục dịch vụ", fields: "Ảnh hoặc icon, nhãn, link." },
      {
        title: "Quy trình",
        fields: "Các bước (tiêu đề), nút đăng ký và link form.",
        source: "Quản lý Form đăng ký",
      },
    ],
  },
  {
    key: "numbers",
    name: "Con số",
    desc: "Ô số liệu kèm ảnh, nhãn và mô tả ngắn.",
    used: "HACOM in numbers",
    thumbId: DESIGN_IMAGE.numbers,
    defaultName: "Con số nổi bật",
    hasEditor: true,
    groups: [
      { title: "Đầu block", fields: "Tiêu đề, phụ đề, mô tả." },
      {
        title: "Ô số",
        fields:
          "Ảnh, con số, nhãn, mô tả, link. Chỉ nhập số liệu đã xác nhận.",
      },
    ],
  },
  {
    key: "news",
    name: "Tin tức",
    desc: "Tab chuyên mục, bài nổi bật, bảng xếp hạng bài viết.",
    used: "Cập nhật công nghệ",
    thumbId: DESIGN_IMAGE.news,
    defaultName: "Tin tức",
    hasEditor: true,
    groups: [
      {
        title: "Tab chuyên mục",
        fields: "Chọn chuyên mục, thứ tự tab.",
        source: "Danh mục bài viết",
      },
      {
        title: "Bài viết",
        fields:
          "Bài lớn: mới nhất hoặc chọn tay. Cột phải: xếp theo lượt xem, số bài.",
        source: "Bài viết",
      },
      {
        title: "Banner và cam kết",
        fields: "Banner phải, dải 4 cam kết, nút Khám phá thêm.",
      },
    ],
  },
  {
    key: "video",
    name: "Video",
    desc: "Danh sách video review dạng trượt ngang.",
    used: "Video review",
    thumbId: DESIGN_IMAGE.video,
    defaultName: "Video review",
    hasEditor: true,
    groups: [
      {
        title: "Nguồn video",
        fields: "Playlist YouTube hoặc danh sách link video, số video hiển thị.",
      },
      {
        title: "Hiển thị",
        fields: "Hiện lượt xem, thời lượng, tên kênh, nút Xem tất cả.",
      },
    ],
  },
  {
    key: "gallery",
    name: "Thư viện ảnh",
    desc: "Ảnh khách hàng kèm lời nhận xét và nơi mua.",
    used: "Khách hàng HACOM",
    thumbId: DESIGN_IMAGE.gallery,
    defaultName: "Khách hàng",
    hasEditor: true,
    groups: [
      {
        title: "Đầu block",
        fields: "Tiêu đề, mô tả, tối đa 4 chỉ số kèm icon, nút xem thêm.",
      },
      {
        title: "Ảnh khách hàng",
        fields: "Ảnh, lời nhận xét, nơi mua. Chọn ảnh lớn đầu tiên.",
        source: "Danh sách ảnh khách hàng",
      },
    ],
  },
  {
    key: "promo",
    name: "Thẻ quảng bá",
    desc: "Thẻ dọc dẫn tới kênh bán hoặc tính năng.",
    used: "Thẻ quảng bá",
    thumbId: DESIGN_IMAGE.promo,
    defaultName: "Thẻ quảng bá",
    hasEditor: true,
    groups: [
      {
        title: "Thẻ",
        fields:
          "Màu chủ đạo, nhãn, tiêu đề, mô tả, ảnh, nút, link, 3 điểm nổi bật. Tối đa 5 thẻ.",
      },
    ],
  },
];

export const templateInfo = (key: HomeBlockTemplate) =>
  TEMPLATES.find((t) => t.key === key) ?? TEMPLATES[0];

const block = (
  id: number,
  name: string,
  template: HomeBlockTemplate,
  code: string,
  thumbId: string,
  extra?: Partial<HomeBlock>
): HomeBlock => ({
  id: String(id),
  name,
  template,
  code,
  thumbId,
  desktop: true,
  mobile: true,
  visible: true,
  from: null,
  to: null,
  ...extra,
});

// 20 block theo thiết kế hacom-new. Không còn là dữ liệu của màn danh sách (lấy từ API TblHome);
// dùng để tra ảnh thiết kế và mẫu block theo mã block.
export const SAMPLE_BLOCKS: HomeBlock[] = [
  block(1, "Banner đầu trang: slider, banner phụ, cam kết", "hero", "home-hero", DESIGN_IMAGE.hero),
  block(2, "Danh mục nổi bật", "catgrid", "home-featured-categories", DESIGN_IMAGE.catgrid),
  block(4, "Chọn đúng máy theo nghề nghiệp", "persona", "home-by-profession", DESIGN_IMAGE.persona),
  block(5, "Laptop - Macbook - Tablet", "product", "home-laptop", DESIGN_IMAGE.laptop),
  block(6, "PC - Mini PC - All in One", "product", "home-pc", DESIGN_IMAGE.pc),
  block(7, "HACOM PC Builder", "banner", "home-pc-builder", DESIGN_IMAGE.pcBuilder),
  block(8, "Màn hình", "product", "home-monitor", DESIGN_IMAGE.monitor),
  block(9, "Linh kiện PC", "tiles", "home-pc-parts", DESIGN_IMAGE.pcParts),
  block(10, "Gaming Gear", "tiles", "home-gaming-gear", DESIGN_IMAGE.gamingGear),
  block(11, "Console - Sim Racing - Giải trí", "tiles", "home-console", DESIGN_IMAGE.console),
  block(12, "Mạng - Lưu trữ - Phần mềm", "tiles", "home-network", DESIGN_IMAGE.network),
  block(13, "Văn phòng - Doanh nghiệp", "product", "home-office", DESIGN_IMAGE.office),
  block(14, "Nhà thông minh", "tiles", "home-smart-home", DESIGN_IMAGE.smartHome),
  block(15, "Khách hàng doanh nghiệp HACOM", "business", "home-business", DESIGN_IMAGE.business, {
    visible: false,
  }),
  block(16, "HACOM dịch vụ tốt", "service", "home-service", DESIGN_IMAGE.service),
  block(17, "HACOM in numbers", "numbers", "home-numbers", DESIGN_IMAGE.numbers),
  block(18, "Cập nhật công nghệ mỗi ngày", "news", "home-news", DESIGN_IMAGE.news),
  block(19, "Video review sản phẩm mới nhất", "video", "home-video", DESIGN_IMAGE.video),
  block(20, "Khách hàng HACOM", "gallery", "home-customers", DESIGN_IMAGE.gallery, {
    mobile: false,
  }),
  block(21, "Thẻ quảng bá: AI, Shopee, TikTok, Build PC", "promo", "home-promo-cards", DESIGN_IMAGE.promo, {
    from: new Date(2026, 10, 1, 0, 0),
    to: new Date(2026, 10, 30, 23, 59),
  }),
];

